/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Public magic-link landing.
 * Flow: AD lands here from outreach email → sees pre-filled listing they
 * never wrote → enters name/email → 1-click "Launch" creates their account,
 * provisions the program, posts the job, redirects them to checkout.
 *
 * Highest-leverage conversion mechanic in the funnel: zero typing required.
 */
export default function ListingClaim() {
  const { token } = useParams();
  const nav = useNavigate();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [adEmail, setAdEmail] = useState("");
  const [adName, setAdName] = useState("");
  const [timing, setTiming] = useState("immediate");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/outreach/claim/${token}`);
        setListing(data);
        if (data.contact_email && !adEmail) setAdEmail(data.contact_email);
        if (data.contact_name && !adName) setAdName(data.contact_name);
        if (data.position_timing) setTiming(data.position_timing);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Link expired or not found");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  async function claim() {
    if (!adEmail || !adName) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/outreach/claim/${token}`, {
        ad_email: adEmail,
        ad_name: adName,
        edits: { position_timing: timing },
      });
      // Stash a flag for the success page to know this came from prefill
      try {
        localStorage.setItem("prefill_lead_claim", JSON.stringify({ at: Date.now(), ...data }));
      } catch {/* ignore */}
      // Prefer the live Stripe checkout URL we just minted on the backend.
      // Falls back to /upgrade if Stripe is in review and we couldn't create one.
      const next = data.checkout_url || data.next_step_url || "/upgrade";
      if (next.startsWith("http")) {
        window.location.href = next;  // external Stripe checkout
      } else {
        nav(next);
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not claim listing — please try again");
      setSubmitting(false);
    }
  }

  if (loading) return <Shell><div className="text-neutral-400">Loading your pre-filled listing…</div></Shell>;
  if (err) return <Shell><div className="text-volt text-lg" data-testid="claim-error">{err}</div></Shell>;
  if (!listing) return null;

  const fac = listing.is_faculty_position || "none";
  const facLabel = fac === "required" ? "Required (teacher + coach)" : fac === "preferred" ? "Preferred (flexible)" : "None (coaching only)";

  return (
    <Shell>
      <div className="text-[11px] tracking-widest text-volt font-bold" data-testid="claim-page-kicker">
        WE PRE-BUILT YOUR LISTING. REVIEW &amp; LAUNCH IN 60 SECONDS.
      </div>
      <h1 className="text-4xl sm:text-5xl mt-3 leading-tight" data-testid="claim-page-title">
        <span className="text-volt">{listing.program_name}</span> &nbsp;·&nbsp; {listing.role}
      </h1>
      <p className="text-neutral-400 mt-3 text-sm leading-relaxed">
        We saw your open coaching position posted publicly via <b>{(listing.source || "your state association").replace(/^state_assoc:\s*/, "")}</b>. Below is the listing we drafted for you on CoachConnect AI. Edit anything you want, then click <b>Launch this listing</b> — we'll handle your account creation + program setup.
      </p>

      {/* Pre-filled listing card */}
      <div className="mt-8 border border-neutral-800 bg-appcard">
        <div className="px-5 py-3 border-b border-neutral-800 text-[10px] tracking-widest text-volt font-bold">
          YOUR PRE-FILLED LISTING (editable after launch)
        </div>
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
          <Row label="Program" value={listing.program_name} />
          <Row label="Role" value={listing.role} />
          <Row label="Gender" value={listing.gender || "—"} />
          <Row label="Level" value={listing.level || "HS"} />
          <Row label="Division" value={listing.division || "—"} />
          <Row label="Location" value={listing.location || "—"} />
          <Row label="Faculty component" value={facLabel} />
          {(fac === "required" || fac === "preferred") && (
            <Row label="Subjects needed" value={(listing.faculty_subjects_needed || []).join(", ") || "Open"} />
          )}
          {listing.salary_band && <Row label="Salary band" value={`$${listing.salary_band}`} />}
          {listing.deadline && <Row label="Application deadline" value={listing.deadline} />}
          <Row label="Position timing" value={timing === "next_season" ? "Starts at the conclusion of the current season" : "Available immediately"} />
        </div>
        {listing.description && (
          <div className="px-5 pb-5 border-t border-neutral-800 pt-4">
            <div className="text-[10px] tracking-widest text-neutral-500 mb-2">JOB DESCRIPTION (scraped)</div>
            <p className="text-sm text-neutral-300 leading-relaxed whitespace-pre-wrap">{listing.description}</p>
          </div>
        )}
      </div>

      {/* Live talent-pool preview — this is what they're buying */}
      {listing.talent_preview && listing.talent_preview.total_matches > 0 && (
        <div className="mt-8 border border-blaze/50 bg-appcard" data-testid="talent-preview">
          <div className="px-5 py-3 border-b border-neutral-800 text-[10px] tracking-widest text-blaze font-bold">
            WHAT YOU'LL SEE WHEN YOU LAUNCH — LIVE FROM OUR TALENT POOL
          </div>
          <div className="p-5 grid grid-cols-2 gap-6">
            <div>
              <div className="heading text-5xl text-white" data-testid="talent-preview-total">{listing.talent_preview.total_matches}</div>
              <div className="text-[11px] tracking-widest text-neutral-400 mt-1 uppercase">
                Coaches fit your criteria ({listing.gender || "—"} · {listing.level || "HS"})
              </div>
            </div>
            <div className={listing.talent_preview.open_to_move > 0 ? "" : "hidden sm:block"}>
              {listing.talent_preview.open_to_move > 0 ? (
                <>
                  <div className="heading text-5xl text-volt" data-testid="talent-preview-open">{listing.talent_preview.open_to_move}</div>
                  <div className="text-[11px] tracking-widest text-neutral-400 mt-1 uppercase">
                    Have signaled they'd consider a move
                  </div>
                </>
              ) : (
                <>
                  <div className="heading text-5xl text-volt" data-testid="talent-preview-tiers">{Object.keys(listing.talent_preview.tier_counts || {}).length}</div>
                  <div className="text-[11px] tracking-widest text-neutral-400 mt-1 uppercase">
                    Talent tiers represented — Elite to Emerging
                  </div>
                </>
              )}
            </div>
          </div>
          {(listing.talent_preview.sample_cards || []).length > 0 && (
            <div className="px-5 pb-5">
              <div className="text-[10px] tracking-widest text-neutral-500 mb-2">TOP-RATED MATCHES (ANONYMIZED SAMPLE)</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {listing.talent_preview.sample_cards.map((c, i) => (
                  <div key={i} className="border border-neutral-800 bg-appbg p-3" data-testid={`talent-sample-card-${i}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] tracking-widest text-blaze font-bold uppercase">{c.tier}</span>
                      <span className="heading text-lg text-white">{c.cir != null ? Math.round(c.cir) : "—"}<span className="text-[9px] text-neutral-500 ml-1">CIR</span></span>
                    </div>
                    <div className="text-xs text-neutral-300 mt-2">{c.current_role || "Coach"} · {c.level || "—"}</div>
                    <div className="text-[10px] text-neutral-500 mt-1">{c.stints} career stint{c.stints === 1 ? "" : "s"}{c.open_to_move ? " · Open to a move" : ""}</div>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-neutral-500 mt-3">Names, full profiles, coaching trees, and direct messaging unlock the moment you launch. No calls, no demos — it's all self-serve.</p>
            </div>
          )}
        </div>
      )}

      {/* Account claim form */}
      <div className="mt-8 border border-volt/40 bg-appcard p-5" data-testid="claim-form">
        <div className="text-[11px] tracking-widest text-volt font-bold">CLAIM YOUR LISTING · LAUNCH WITH FOUNDING MEMBER PRICING</div>
        <div className="mt-3 p-3 bg-appbg border border-neutral-800 text-xs text-neutral-300 leading-relaxed">
          {(listing.level || "HS") === "HS"
            ? <>Your free <b className="text-white">Program Basic</b> account posts unlimited jobs and manages every applicant. Add <b className="text-volt">Program Recruiting — $5/month</b> for proactive talent search, matching and market intel. Cancel anytime.</>
            : <>Your free <b className="text-white">Program Basic</b> account posts unlimited jobs and manages every applicant. Add <b className="text-volt">Program Recruiting — $5/month</b> for proactive talent search, matching and market intel. Cancel anytime.</>}
        </div>
        <div className="mt-4">
          <div className="text-[10px] tracking-widest text-neutral-500 mb-1">WHEN IS THIS POSITION AVAILABLE?</div>
          <select
            data-testid="claim-timing"
            className="bg-appbg border border-neutral-800 px-3 py-3 text-sm text-white w-full sm:w-auto"
            value={timing}
            onChange={(e) => setTiming(e.target.value)}
          >
            <option value="immediate">Available immediately</option>
            <option value="next_season">Starts at the conclusion of the current season</option>
          </select>
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input
            data-testid="claim-name"
            className="bg-appbg border border-neutral-800 px-3 py-3 text-sm text-white"
            value={adName}
            onChange={(e) => setAdName(e.target.value)}
            placeholder="Your full name (e.g. Matt Pobieglo)"
          />
          <input
            data-testid="claim-email"
            type="email"
            className="bg-appbg border border-neutral-800 px-3 py-3 text-sm text-white"
            value={adEmail}
            onChange={(e) => setAdEmail(e.target.value)}
            placeholder="Your AD/contact email"
          />
        </div>
        <p className="text-[11px] text-neutral-500 mt-3 leading-relaxed">
          We'll email you a temporary password to your inbox. You can edit any listing detail after launch, including faculty subjects, salary, and description. No card required to preview — payment happens on the next page.
        </p>
        <button
          data-testid="launch-listing-btn"
          disabled={!adEmail || !adName || submitting}
          onClick={claim}
          className="mt-4 bg-volt text-black px-5 min-h-[44px] text-xs tracking-widest font-bold border border-volt hover:bg-white hover:text-volt transition-colors disabled:opacity-50"
        >
          {submitting ? "LAUNCHING…" : "LAUNCH THIS LISTING →"}
        </button>
      </div>

      <p className="text-[11px] text-neutral-600 mt-8" data-testid="claim-footer">
        Not interested? Just ignore this email. We won't follow up. CoachConnect AI is built by a father-and-son family team — a 30-year HS head coach + state champion, and his son (HS All-American · D1 + D3 college player). We've sat in your seat.
      </p>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-appbg text-white">
      <div className="max-w-3xl mx-auto px-6 py-12 sm:py-20">
        <div className="text-[11px] tracking-[3px] text-volt font-bold">COACHCONNECT AI</div>
        <div className="mt-10">{children}</div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <div className="text-[10px] tracking-widest text-neutral-500">{label.toUpperCase()}</div>
      <div className="text-sm text-white mt-1">{value}</div>
    </div>
  );
}
